<template>
    <div class="rtp-dashboard-embedded">
        <!-- Sub-Tab Navigation -->
        <div class="rtp-sub-tabs">
            <button v-if="playerHasActiveRTP || isPlayer"
                :class="['sub-tab-btn', { active: activeSubTab === 'my-exercises' }]"
                @click="activeSubTab = 'my-exercises'">
                <i class="bi bi-list-check"></i>
                My Exercises
            </button>
            <button v-if="canViewDashboard" :class="['sub-tab-btn', { active: activeSubTab === 'squad-dashboard' }]"
                @click="activeSubTab = 'squad-dashboard'">
                <i class="bi bi-speedometer2"></i>
                Squad Dashboard
            </button>
            <button v-if="canManageExercises" :class="['sub-tab-btn', { active: activeSubTab === 'exercise-library' }]"
                @click="activeSubTab = 'exercise-library'">
                <i class="bi bi-journal-medical"></i>
                Exercise Library
            </button>
        </div>

        <!-- My Exercises Tab (for players with active RTP) -->
        <div v-if="activeSubTab === 'my-exercises'" class="my-exercises-content">
            <MyRTP :playerId="userData?.id" />
        </div>

        <!-- Exercise Library Tab -->
        <div v-if="activeSubTab === 'exercise-library'" class="exercise-library-content">
            <RTPExerciseLibrary />
        </div>

        <!-- Squad Dashboard Tab -->
        <div v-if="activeSubTab === 'squad-dashboard'" class="squad-dashboard-content">
            <!-- Header -->
            <div class="dashboard-header">
                <div class="header-info">
                    <h3>
                        <i class="bi bi-activity"></i>
                        Return-to-Play Protocols
                    </h3>
                    <p>Monitor and manage player rehabilitation phases</p>
                </div>
                <div class="header-actions">
                    <button class="btn btn-outline-primary btn-sm" @click="refreshData">
                        <i class="bi bi-arrow-clockwise"></i> Refresh
                    </button>
                    <button class="btn btn-outline-secondary btn-sm" @click="exportToCsv">
                        <i class="bi bi-download"></i> Export
                    </button>
                </div>
            </div>

            <!-- Summary Stats -->
            <div class="summary-stats">
                <div class="stat-card total">
                    <div class="stat-icon">
                        <i class="bi bi-people"></i>
                    </div>
                    <div class="stat-content">
                        <span class="stat-value">{{ summary.total }}</span>
                        <span class="stat-label">Active RTPs</span>
                    </div>
                </div>

                <div v-for="phase in 5" :key="phase" class="stat-card" :class="'phase-' + phase">
                    <div class="stat-icon"
                        :style="{ backgroundColor: getPhaseInfo(phase).bgColor, color: getPhaseInfo(phase).color }">
                        <span>P{{ phase }}</span>
                    </div>
                    <div class="stat-content">
                        <span class="stat-value">{{ summary.byPhase[phase] || 0 }}</span>
                        <span class="stat-label">{{ getPhaseInfo(phase).name }}</span>
                    </div>
                </div>

                <div class="stat-card overdue" v-if="summary.overdueReviews > 0">
                    <div class="stat-icon">
                        <i class="bi bi-exclamation-triangle"></i>
                    </div>
                    <div class="stat-content">
                        <span class="stat-value">{{ summary.overdueReviews }}</span>
                        <span class="stat-label">Overdue Reviews</span>
                    </div>
                </div>

                <div class="stat-card circa-phv" v-if="summary.circaPhvCount > 0">
                    <div class="stat-icon">
                        <i class="bi bi-person-exclamation"></i>
                    </div>
                    <div class="stat-content">
                        <span class="stat-value">{{ summary.circaPhvCount }}</span>
                        <span class="stat-label">Circa-PHV Players</span>
                    </div>
                </div>
            </div>

            <!-- Filters -->
            <div class="filters-section">
                <div class="filter-group">
                    <label>Team:</label>
                    <select v-model="selectedTeam" class="form-select" @change="loadData">
                        <option value="">All Teams</option>
                        <option v-for="team in teams" :key="team.id" :value="team.id">
                            {{ team.teamName }}
                        </option>
                    </select>
                </div>

                <div class="filter-group">
                    <label>Phase:</label>
                    <select v-model="filterPhase" class="form-select">
                        <option value="">All Phases</option>
                        <option v-for="phase in 5" :key="phase" :value="phase">
                            P{{ phase }}: {{ getPhaseInfo(phase).name }}
                        </option>
                    </select>
                </div>

                <div class="filter-group">
                    <label>View:</label>
                    <div class="btn-group view-toggle">
                        <button :class="['btn', viewMode === 'grouped' ? 'btn-primary' : 'btn-outline-primary']"
                            @click="viewMode = 'grouped'">
                            <i class="bi bi-collection"></i> Grouped
                        </button>
                        <button :class="['btn', viewMode === 'table' ? 'btn-primary' : 'btn-outline-primary']"
                            @click="viewMode = 'table'">
                            <i class="bi bi-table"></i> Table
                        </button>
                    </div>
                </div>
            </div>

            <!-- Loading -->
            <div v-if="loading" class="loading-state">
                <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">Loading...</span>
                </div>
                <p>Loading RTP data...</p>
            </div>

            <!-- Empty State -->
            <div v-else-if="filteredProtocols.length === 0" class="empty-state">
                <i class="bi bi-clipboard-check"></i>
                <h4>No Active RTP Protocols</h4>
                <p>There are no players currently on a Return-to-Play protocol.</p>
                <p class="text-muted mt-3">
                    <small>To start an RTP protocol, go to a player's injury record and click "Start RTP
                        Protocol".</small>
                </p>
            </div>

            <!-- Grouped View -->
            <div v-else-if="viewMode === 'grouped'" class="grouped-view">
                <div v-for="phase in 5" :key="phase" v-show="!filterPhase || filterPhase == phase" class="phase-group"
                    :class="{ 'empty': !groupedProtocols[phase] || groupedProtocols[phase].length === 0 }">
                    <div class="group-header" :style="{ borderLeftColor: getPhaseInfo(phase).color }">
                        <div class="group-title">
                            <span class="phase-badge" :style="{ backgroundColor: getPhaseInfo(phase).color }">
                                P{{ phase }}
                            </span>
                            <h3>{{ getPhaseInfo(phase).name }}</h3>
                            <span class="player-count">
                                {{ (groupedProtocols[phase] || []).length }} player{{ (groupedProtocols[phase] ||
                                    []).length !== 1 ? 's' : '' }}
                            </span>
                        </div>
                        <p class="group-description">{{ getPhaseInfo(phase).description }}</p>
                    </div>

                    <div v-if="(groupedProtocols[phase] || []).length > 0" class="players-grid">
                        <div v-for="player in groupedProtocols[phase]" :key="player.injury_id" class="player-card"
                            :class="{
                                'overdue': player.days_until_review < 0,
                                'circa-phv': player.maturity_at_injury === 'Circa-PHV'
                            }" @click="openPlayerModal(player)">
                            <div class="card-header">
                                <img v-if="player.player_image" :src="getImgUrl(player.player_image)"
                                    :alt="player.player_name" class="player-avatar" />
                                <div v-else class="player-avatar-placeholder">
                                    {{ getInitials(player.player_name) }}
                                </div>
                                <div class="player-info">
                                    <span class="player-name">{{ player.player_name }}</span>
                                    <span class="team-name">{{ player.teamName || player.age_group }}</span>
                                </div>
                                <div v-if="player.maturity_at_injury === 'Circa-PHV'" class="maturity-flag">
                                    <i class="bi bi-exclamation-triangle"></i>
                                </div>
                            </div>

                            <div class="card-body">
                                <div class="injury-type">
                                    <i class="bi bi-bandaid"></i>
                                    {{ player.injury_type }}
                                </div>

                                <div class="card-stats">
                                    <div class="stat">
                                        <span class="stat-label">Days in Phase</span>
                                        <span class="stat-value" :class="{ 'warning': player.days_in_phase > 14 }">
                                            {{ player.days_in_phase || 0 }}
                                        </span>
                                    </div>
                                    <div class="stat">
                                        <span class="stat-label">Phase Lead</span>
                                        <span class="stat-value">{{ player.current_phase_lead || '-' }}</span>
                                    </div>
                                </div>

                                <div v-if="player.days_until_review !== null" class="review-status"
                                    :class="{ 'overdue': player.days_until_review < 0 }">
                                    <i
                                        :class="player.days_until_review < 0 ? 'bi bi-exclamation-circle' : 'bi bi-calendar-check'"></i>
                                    <span v-if="player.days_until_review < 0">
                                        Review overdue by {{ Math.abs(player.days_until_review) }} days
                                    </span>
                                    <span v-else-if="player.days_until_review === 0">
                                        Review due today
                                    </span>
                                    <span v-else>
                                        Review in {{ player.days_until_review }} days
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-else class="no-players">
                        <p>No players in this phase</p>
                    </div>
                </div>
            </div>

            <!-- Table View -->
            <div v-else class="table-view">
                <table class="rtp-list-table">
                    <thead>
                        <tr>
                            <th>Player</th>
                            <th>Team</th>
                            <th>Injury</th>
                            <th>Phase</th>
                            <th>Days</th>
                            <th>Review</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="player in protocols" :key="player.injury_id"
                            :class="{ 'overdue-row': player.days_until_review < 0 }" @click="openPlayerModal(player)">
                            <td>
                                <div class="player-cell">
                                    <img v-if="player.player_image" :src="getImgUrl(player.player_image)"
                                        class="avatar-sm" />
                                    <div v-else class="avatar-placeholder-sm">
                                        {{ getInitials(player.player_name) }}
                                    </div>
                                    {{ player.player_name }}
                                    <span v-if="player.maturity_at_injury === 'Circa-PHV'" class="maturity-icon">
                                        <i class="bi bi-exclamation-triangle"></i>
                                    </span>
                                </div>
                            </td>
                            <td>{{ player.teamName || player.age_group }}</td>
                            <td>{{ player.injury_type }}</td>
                            <td>
                                <span class="phase-badge-sm"
                                    :style="{ backgroundColor: getPhaseColor(player.rtp_phase) }">
                                    P{{ player.rtp_phase }}
                                </span>
                            </td>
                            <td>{{ player.days_in_phase || 0 }}</td>
                            <td :class="{ 'text-danger': player.days_until_review < 0 }">
                                {{ player.days_until_review !== null
                                    ? (player.days_until_review < 0 ? Math.abs(player.days_until_review) + 'd overdue' :
                                        player.days_until_review + 'd') : '-' }} </td>
                            <td>
                                <button class="btn btn-sm btn-outline-primary">
                                    <i class="bi bi-eye"></i>
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Player Detail Modal -->
            <div v-if="showPlayerModal && selectedPlayer" class="rtp-modal-overlay" @click.self="closePlayerModal">
                <div class="rtp-modal-dialog">
                    <div class="rtp-modal-header">
                        <h4>{{ selectedPlayer.player_name }} - RTP Protocol</h4>
                        <button class="rtp-modal-close" @click="closePlayerModal">&times;</button>
                    </div>
                    <div class="rtp-modal-body">
                        <!-- Player Header -->
                        <div class="player-header-bar">
                            <div class="player-profile">
                                <img v-if="selectedPlayer.player_image" :src="getImgUrl(selectedPlayer.player_image)"
                                    :alt="selectedPlayer.player_name" class="profile-avatar" />
                                <div v-else class="profile-avatar-placeholder">
                                    {{ getInitials(selectedPlayer.player_name) }}
                                </div>
                                <div class="profile-info">
                                    <span class="player-name">{{ selectedPlayer.player_name }}</span>
                                    <span class="team-name">{{ selectedPlayer.teamName || selectedPlayer.age_group
                                    }}</span>
                                </div>
                            </div>
                            <div class="injury-summary">
                                <span class="injury-type-badge"><i class="bi bi-bandaid"></i> {{
                                    selectedPlayer.injury_type }}</span>
                                <span class="phase-badge"
                                    :style="{ backgroundColor: getPhaseColor(selectedPlayer.rtp_phase) }">
                                    Phase {{ selectedPlayer.rtp_phase }}
                                </span>
                            </div>
                        </div>

                        <!-- Status Bar -->
                        <div class="status-bar">
                            <div class="status-item">
                                <label>Playing Status:</label>
                                <select v-model="selectedPlayer.playing_status"
                                    @change="updatePlayerStatus('playing_status', selectedPlayer.playing_status)"
                                    class="form-select form-select-sm">
                                    <option :value="0">Can't play</option>
                                    <option :value="1">Ready to play</option>
                                </select>
                            </div>
                            <div class="status-item">
                                <label>Training Status:</label>
                                <select v-model="selectedPlayer.training_status"
                                    @change="updatePlayerStatus('training_status', selectedPlayer.training_status)"
                                    class="form-select form-select-sm">
                                    <option value="Unable">Unable to train</option>
                                    <option value="Partially">Can partially train</option>
                                    <option value="Fully">Can fully train</option>
                                </select>
                            </div>
                            <div v-if="selectedPlayer.estimate_return_date" class="status-item">
                                <label>Est. Return:</label>
                                <span class="status-value">{{ selectedPlayer.estimate_return_date }}</span>
                            </div>
                        </div>

                        <!-- Tabs -->
                        <ul class="nav nav-tabs detail-tabs">
                            <li class="nav-item">
                                <a class="nav-link" :class="{ active: modalTab === 'tracker' }"
                                    @click="modalTab = 'tracker'" href="javascript:;">
                                    <i class="bi bi-speedometer2"></i> Phase Tracker
                                </a>
                            </li>
                            <li class="nav-item">
                                <a class="nav-link" :class="{ active: modalTab === 'progress' }"
                                    @click="modalTab = 'progress'" href="javascript:;">
                                    <i class="bi bi-graph-up"></i> Progress & Exercises
                                </a>
                            </li>
                            <li class="nav-item">
                                <a class="nav-link" :class="{ active: modalTab === 'timeline' }"
                                    @click="modalTab = 'timeline'" href="javascript:;">
                                    <i class="bi bi-clock-history"></i> Timeline
                                </a>
                            </li>
                            <li class="nav-item">
                                <a class="nav-link" :class="{ active: modalTab === 'exercises' }"
                                    @click="modalTab = 'exercises'" href="javascript:;">
                                    <i class="bi bi-journal-medical"></i> Exercise Library
                                </a>
                            </li>
                            <li class="nav-item">
                                <a class="nav-link" :class="{ active: modalTab === 'injury-details' }"
                                    @click="modalTab = 'injury-details'" href="javascript:;">
                                    <i class="bi bi-file-medical"></i> Injury Details
                                </a>
                            </li>
                            <li class="nav-item">
                                <a class="nav-link" :class="{ active: modalTab === 'physio-appointments' }"
                                    @click="modalTab = 'physio-appointments'; loadPlayerAppointments()"
                                    href="javascript:;">
                                    <i class="bi bi-calendar2-heart"></i> Physio
                                    <span v-if="playerAppointments.length > 0" class="appointment-badge">{{
                                        playerAppointments.length }}</span>
                                </a>
                            </li>
                        </ul>

                        <div class="modal-content-area">
                            <RTPPhaseTracker v-if="modalTab === 'tracker' && selectedPlayer.injury_id"
                                :injuryId="selectedPlayer.injury_id" :userData="userData"
                                @phase-updated="onPhaseUpdated" @rtp-completed="onRtpCompleted" />

                            <PlayerRTPView v-if="modalTab === 'progress' && selectedPlayer.injury_id"
                                :injuryId="selectedPlayer.injury_id" :playerId="selectedPlayer.player_id"
                                :hidePhaseProgress="true" :hideCurrentPhaseCard="true" />

                            <RTPTimeline v-if="modalTab === 'timeline'" :injuryDate="selectedPlayer.injury_date"
                                :injuryType="selectedPlayer.injury_type"
                                :injuryReportedBy="{ name: selectedPlayer.injury_reported_by_name, role: selectedPlayer.injury_reported_by_role }"
                                :rtpStartDate="selectedPlayer.rtp_start_date"
                                :maturityAtInjury="selectedPlayer.maturity_at_injury" :phaseHistory="playerPhaseHistory"
                                :currentPhase="selectedPlayer.rtp_phase"
                                :daysInCurrentPhase="selectedPlayer.days_in_phase"
                                :targetDate="selectedPlayer.rtp_target_date" />

                            <RTPExerciseLibrary v-if="modalTab === 'exercises'" :currentPhase="selectedPlayer.rtp_phase"
                                :maturityCategory="selectedPlayer.maturity_at_injury"
                                :injuryId="selectedPlayer.injury_id" :playerId="selectedPlayer.player_id"
                                :playerName="selectedPlayer.player_name" assignmentMode />

                            <!-- Injury Details Tab -->
                            <div v-if="modalTab === 'injury-details'" class="injury-details-content">
                                <div class="detail-section">
                                    <h5><i class="bi bi-bandaid"></i> Injury Information</h5>
                                    <div class="detail-grid">
                                        <div class="detail-item">
                                            <label>Injury Type:</label>
                                            <span>{{ selectedPlayer.injury_type || '-' }}</span>
                                        </div>
                                        <div class="detail-item">
                                            <label>Diagnosis:</label>
                                            <span>{{ selectedPlayer.diagnosis || '-' }}</span>
                                        </div>
                                        <div class="detail-item">
                                            <label>Activity at Time:</label>
                                            <span>{{ selectedPlayer.activity_at_time || '-' }}</span>
                                        </div>
                                        <div class="detail-item">
                                            <label>Injury Date:</label>
                                            <span>{{ formatDate(selectedPlayer.injury_date) }}</span>
                                        </div>
                                        <div class="detail-item">
                                            <label>Reported By:</label>
                                            <span>{{ selectedPlayer.injury_reported_by_name || '-' }} ({{
                                                selectedPlayer.injury_reported_by_role || '-' }})</span>
                                        </div>
                                        <div class="detail-item">
                                            <label>Est. Return:</label>
                                            <span>{{ selectedPlayer.estimate_return_date || '-' }}</span>
                                        </div>
                                        <div v-if="selectedPlayer.treatment" class="detail-item">
                                            <label>Treatment:</label>
                                            <span>{{ selectedPlayer.treatment }}</span>
                                        </div>
                                    </div>
                                </div>

                                <div class="detail-section">
                                    <h5><i class="bi bi-activity"></i> Current Status</h5>
                                    <div class="detail-grid">
                                        <div class="detail-item">
                                            <label>Playing Status:</label>
                                            <span
                                                :class="selectedPlayer.playing_status == 1 ? 'status-good' : 'status-bad'">
                                                {{ selectedPlayer.playing_status == 1 ? 'Ready to play' : "Can't play"
                                                }}
                                            </span>
                                        </div>
                                        <div class="detail-item">
                                            <label>Training Status:</label>
                                            <span :class="{
                                                'status-good': selectedPlayer.training_status === 'Fully',
                                                'status-warning': selectedPlayer.training_status === 'Partially',
                                                'status-bad': selectedPlayer.training_status === 'Unable' || !selectedPlayer.training_status
                                            }">
                                                {{ selectedPlayer.training_status || 'None' }}
                                            </span>
                                        </div>
                                        <div class="detail-item">
                                            <label>RTP Phase:</label>
                                            <span class="phase-badge-inline"
                                                :style="{ backgroundColor: getPhaseColor(selectedPlayer.rtp_phase) }">
                                                Phase {{ selectedPlayer.rtp_phase }} - {{
                                                    getPhaseInfo(selectedPlayer.rtp_phase).name }}
                                            </span>
                                        </div>
                                        <div class="detail-item">
                                            <label>Days on RTP:</label>
                                            <span>{{ selectedPlayer.rtp_start_date ?
                                                getDaysSince(selectedPlayer.rtp_start_date) : '-' }} days</span>
                                        </div>
                                    </div>
                                </div>

                                <div v-if="selectedPlayer.injury_notes" class="detail-section">
                                    <h5><i class="bi bi-journal-text"></i> Notes</h5>
                                    <p class="injury-notes">{{ selectedPlayer.injury_notes }}</p>
                                </div>

                                <div v-if="selectedPlayer.rtp_restrictions" class="detail-section">
                                    <h5><i class="bi bi-exclamation-triangle"></i> Restrictions</h5>
                                    <p class="restrictions-text">{{ selectedPlayer.rtp_restrictions }}</p>
                                </div>
                            </div>

                            <!-- Physio Appointments Tab -->
                            <div v-if="modalTab === 'physio-appointments'" class="physio-appointments-content">
                                <div class="appointments-header">
                                    <h5><i class="bi bi-calendar2-heart"></i> Physio Appointments</h5>
                                    <router-link :to="{ name: 'bookPhysio' }" class="btn btn-sm btn-success">
                                        <i class="bi bi-plus"></i> Book Appointment
                                    </router-link>
                                </div>

                                <div v-if="appointmentsLoading" class="loading-state">
                                    <div class="spinner-border spinner-border-sm text-primary"></div>
                                    <span>Loading appointments...</span>
                                </div>

                                <div v-else-if="playerAppointments.length === 0" class="empty-appointments">
                                    <i class="bi bi-calendar-x"></i>
                                    <p>No physio appointments booked</p>
                                    <router-link :to="{ name: 'bookPhysio' }" class="btn btn-primary btn-sm">
                                        Book an Appointment
                                    </router-link>
                                </div>

                                <div v-else class="appointments-list">
                                    <div v-for="apt in playerAppointments" :key="apt.id" class="appointment-card"
                                        :class="apt.status">
                                        <div class="apt-date-badge">
                                            <span class="apt-day">{{ formatAptDay(apt.appointment_date) }}</span>
                                            <span class="apt-month">{{ formatAptMonth(apt.appointment_date) }}</span>
                                        </div>
                                        <div class="apt-details">
                                            <div class="apt-time">
                                                <i class="bi bi-clock"></i>
                                                {{ formatTime(apt.start_time) }} - {{ formatTime(apt.end_time) }}
                                            </div>
                                            <div class="apt-physio">
                                                <i class="bi bi-person-badge"></i>
                                                {{ apt.physio_name || 'Physio' }}
                                            </div>
                                            <div v-if="apt.reason" class="apt-reason">
                                                <i class="bi bi-chat-text"></i>
                                                {{ apt.reason }}
                                            </div>
                                        </div>
                                        <div class="apt-status">
                                            <span class="status-badge" :class="apt.status">
                                                {{ apt.status }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div><!-- end squad-dashboard-content -->
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import RTPPhaseTracker from "@/components/RTPPhaseTracker/index.vue";
import RTPTimeline from "@/components/RTPTimeline/index.vue";
import RTPExerciseLibrary from "@/components/RTPExerciseLibrary/index.vue";
import PlayerRTPView from "@/components/PlayerRTPView/index.vue";
import MyRTP from "@/components/MyRTP/index.vue";
import { ReturnToPlayService, RTP_PHASES } from "@/services/rtp-service.js";
import { Teams } from "@/services/teams-service.js";
import { PhysioCalendarService } from "@/services/physio-calendar-service.js";

export default {
    name: "ReturnToPlayDashboard",
    components: {
        RTPPhaseTracker,
        RTPTimeline,
        RTPExerciseLibrary,
        PlayerRTPView,
        MyRTP
    },
    data() {
        return {
            loading: true,
            protocols: [],
            summary: {
                total: 0,
                byPhase: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
                overdueReviews: 0,
                circaPhvCount: 0
            },
            teams: [],
            selectedTeam: '',
            filterPhase: '',
            viewMode: 'grouped',
            selectedPlayer: null,
            showPlayerModal: false,
            modalTab: 'tracker',
            playerPhaseHistory: [],
            userData: null,
            // Sub-tab state
            activeSubTab: 'squad-dashboard',
            playerHasActiveRTP: false,
            // Physio appointments
            playerAppointments: [],
            appointmentsLoading: false
        };
    },
    computed: {
        ...mapGetters(["getProfile"]),
        isPlayer() {
            if (!this.userData) return false;
            const staffRoles = ['Admin', 'Coach', 'HeadCoach', 'Physio', 'Head Physio', 'Sports Science', 'Head Sports Science', 'Scout'];
            return !staffRoles.includes(this.userData.userType) && !staffRoles.includes(this.userData.position);
        },
        canViewDashboard() {
            if (!this.userData) return false;
            const allowedRoles = ['Admin', 'Coach', 'HeadCoach', 'Physio', 'Head Physio', 'Sports Science', 'Head Sports Science'];
            return allowedRoles.includes(this.userData.userType) || allowedRoles.includes(this.userData.position);
        },
        canManageExercises() {
            if (!this.userData) return false;
            const allowedRoles = ['Admin', 'Physio', 'Head Physio', 'Sports Science', 'Head Sports Science'];
            return allowedRoles.includes(this.userData.userType) || allowedRoles.includes(this.userData.position);
        },
        filteredProtocols() {
            let result = [...this.protocols];
            if (this.filterPhase) {
                result = result.filter(p => p.rtp_phase == this.filterPhase);
            }
            return result;
        },
        groupedProtocols() {
            const grouped = { 1: [], 2: [], 3: [], 4: [], 5: [] };
            this.protocols.forEach(player => {
                if (grouped[player.rtp_phase]) {
                    grouped[player.rtp_phase].push(player);
                }
            });
            return grouped;
        }
    },
    async mounted() {
        // Get user data from Vuex store
        if (this.getProfile !== null) {
            this.userData = { ...this.getProfile };
        }

        if (!this.userData?.clubId) {
            this.loading = false;
            return;
        }

        // Check if user is a player with active RTP
        if (this.isPlayer) {
            await this.checkPlayerActiveRTP();
            // Default to My Exercises tab for players with active RTP
            if (this.playerHasActiveRTP) {
                this.activeSubTab = 'my-exercises';
            }
        }

        // Set default tab based on role
        if (!this.canViewDashboard && this.playerHasActiveRTP) {
            this.activeSubTab = 'my-exercises';
        } else if (this.canViewDashboard) {
            this.activeSubTab = 'squad-dashboard';
        }

        await this.loadTeams();
        await this.loadData();
    },
    methods: {
        getPhaseColor(phase) {
            const colors = {
                1: '#dc3545',
                2: '#fd7e14',
                3: '#ffc107',
                4: '#20c997',
                5: '#28a745'
            };
            return colors[phase] || '#6c757d';
        },

        getInitials(name) {
            if (!name) return '?';
            return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
        },
        getImgUrl(pic) {
            return pic !== undefined && pic !== "" ? `/images/${pic}` : "";
        },

        getPhaseInfo(phase) {
            return RTP_PHASES[phase] || { name: 'Unknown', color: '#6c757d', bgColor: '#f8f9fa', description: '' };
        },

        formatDate(dateStr) {
            if (!dateStr) return '-';
            const date = new Date(dateStr);
            return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
        },

        getDaysSince(dateStr) {
            if (!dateStr) return 0;
            const date = new Date(dateStr);
            const today = new Date();
            const diffTime = today - date;
            return Math.floor(diffTime / (1000 * 60 * 60 * 24));
        },

        async updatePlayerStatus(field, value) {
            if (!this.selectedPlayer?.injury_id) return;

            try {
                const data = {
                    injuryId: this.selectedPlayer.injury_id
                };

                if (field === 'playing_status') {
                    data.playingStatus = value;
                } else if (field === 'training_status') {
                    data.trainingStatus = value;
                }

                const response = await ReturnToPlayService.updateRTPStatus(data);
                if (response.data?.success) {
                    this.$toast?.success('Status updated successfully');
                    // Also update in the protocols array
                    const idx = this.protocols.findIndex(p => p.injury_id === this.selectedPlayer.injury_id);
                    if (idx !== -1) {
                        this.protocols[idx][field] = value;
                    }
                } else {
                    this.$toast?.error(response.data?.message || 'Failed to update status');
                }
            } catch (error) {
                console.error('Error updating status:', error);
                this.$toast?.error('Error updating status');
            }
        },

        async loadTeams() {
            if (!this.userData?.clubId) return;

            try {
                const response = await Teams.find({
                    action: "getTeamsDataById",
                    clubId: this.userData.clubId
                });
                this.teams = response.data || [];
            } catch (error) {
                console.error('Error loading teams:', error);
            }
        },

        async loadData() {
            if (!this.userData?.clubId) {
                this.loading = false;
                return;
            }

            this.loading = true;
            try {
                const params = { clubId: this.userData.clubId };
                if (this.selectedTeam) {
                    params.teamId = this.selectedTeam;
                }

                const response = await ReturnToPlayService.getRTPDashboard(params);

                if (response.data.success) {
                    this.protocols = response.data.protocols || [];
                    this.summary = response.data.summary || {
                        total: 0,
                        byPhase: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
                        overdueReviews: 0,
                        circaPhvCount: 0
                    };

                    // Emit count for parent badge
                    this.$emit('rtp-count-updated', this.summary.total);
                }
            } catch (error) {
                console.error('Error loading RTP data:', error);
            } finally {
                this.loading = false;
            }
        },

        refreshData() {
            this.loadData();
        },

        async checkPlayerActiveRTP() {
            if (!this.userData?.id) return;
            try {
                const response = await ReturnToPlayService.getPlayerActiveRTP(this.userData.id);
                this.playerHasActiveRTP = response.data.success && response.data.has_active_rtp;
            } catch (error) {
                console.error('Error checking player RTP:', error);
                this.playerHasActiveRTP = false;
            }
        },

        exportToCsv() {
            const headers = ['Player', 'Team', 'Injury', 'Phase', 'Days in Phase', 'Phase Lead', 'Maturity', 'Review Status'];
            const rows = this.protocols.map(p => [
                p.player_name,
                p.teamName || p.age_group,
                p.injury_type,
                `P${p.rtp_phase}`,
                p.days_in_phase || 0,
                p.current_phase_lead || '-',
                p.maturity_at_injury || '-',
                p.days_until_review !== null ? (p.days_until_review < 0 ? `${Math.abs(p.days_until_review)}d overdue` : `${p.days_until_review}d`) : '-'
            ]);

            const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

            this.$nextTick(() => {
                const doc = window.document || document;
                if (typeof doc?.createElement === 'function') {
                    const link = doc.createElement('a');
                    link.href = URL.createObjectURL(blob);
                    link.download = `rtp-dashboard-${new Date().toISOString().split('T')[0]}.csv`;
                    link.click();
                }
            });
        },

        async openPlayerModal(player) {
            this.selectedPlayer = player;
            this.modalTab = 'tracker';
            this.showPlayerModal = true;
            this.playerAppointments = []; // Reset appointments

            // Load phase history
            try {
                const response = await ReturnToPlayService.getRTPByInjuryId(player.injury_id);
                if (response.data.success) {
                    this.playerPhaseHistory = response.data.phase_history || [];
                }
            } catch (error) {
                console.error('Error loading phase history:', error);
            }

            // Pre-load physio appointments for badge count
            this.loadPlayerAppointments();
        },

        closePlayerModal() {
            this.showPlayerModal = false;
            this.selectedPlayer = null;
            this.playerAppointments = [];
            this.playerPhaseHistory = [];
        },

        onPhaseUpdated() {
            this.loadData();
            if (this.selectedPlayer) {
                this.openPlayerModal(this.selectedPlayer);
            }
        },

        onRtpCompleted() {
            this.showPlayerModal = false;
            this.selectedPlayer = null;
            this.loadData();
        },

        async loadPlayerAppointments() {
            if (!this.selectedPlayer?.player_id) return;

            this.appointmentsLoading = true;
            try {
                console.log('Loading appointments for player_id:', this.selectedPlayer.player_id);
                const response = await PhysioCalendarService.getPlayerAppointments({
                    player_id: this.selectedPlayer.player_id
                });
                console.log('Appointments response:', response.data);

                // API returns array directly, not wrapped in {success, appointments}
                if (Array.isArray(response.data)) {
                    this.playerAppointments = response.data;
                } else if (response.data?.appointments) {
                    this.playerAppointments = response.data.appointments;
                } else {
                    this.playerAppointments = [];
                }
                console.log('Loaded', this.playerAppointments.length, 'appointments');
            } catch (error) {
                console.error('Error loading player appointments:', error);
                this.playerAppointments = [];
            } finally {
                this.appointmentsLoading = false;
            }
        },

        formatAptDay(dateStr) {
            if (!dateStr) return '';
            const date = new Date(dateStr);
            return date.getDate();
        },

        formatAptMonth(dateStr) {
            if (!dateStr) return '';
            const date = new Date(dateStr);
            return date.toLocaleDateString('en-GB', { month: 'short' });
        },

        formatTime(timeStr) {
            if (!timeStr) return '';
            const [hours, minutes] = timeStr.split(':');
            const hour = parseInt(hours);
            const ampm = hour >= 12 ? 'pm' : 'am';
            const hour12 = hour % 12 || 12;
            return `${hour12}:${minutes}${ampm}`;
        }
    }
};
</script>

<style scoped lang="scss">
.rtp-dashboard-embedded {
    padding: 20px;
    width: 100%;
}

/* Sub-Tab Navigation */
.rtp-sub-tabs {
    display: flex;
    gap: 8px;
    margin-bottom: 24px;
    padding: 4px;
    background: #f8f9fa;
    border-radius: 10px;
    width: fit-content;
}

.sub-tab-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    border: none;
    background: transparent;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    color: #6c757d;
    cursor: pointer;
    transition: all 0.2s ease;

    i {
        font-size: 16px;
    }

    &:hover {
        background: rgba(0, 123, 255, 0.1);
        color: #007bff;
    }

    &.active {
        background: white;
        color: #007bff;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    }
}

.my-exercises-content,
.exercise-library-content {
    background: white;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.squad-dashboard-content {
    /* Wrapper for the squad dashboard content */
}

/* Custom Modal Styles */
.rtp-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1050;
    padding: 20px;
}

.rtp-modal-dialog {
    background: white;
    border-radius: 12px;
    width: 100%;
    max-width: 100%;
    max-height: 90vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

.rtp-modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid #e9ecef;

    h4 {
        margin: 0;
        font-size: 18px;
        font-weight: 600;
    }
}

.rtp-modal-close {
    background: none;
    border: none;
    font-size: 28px;
    cursor: pointer;
    color: #6c757d;
    line-height: 1;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;

    &:hover {
        background: #f8f9fa;
        color: #333;
    }
}

.rtp-modal-body {
    padding: 20px;
    overflow-y: auto;
    flex: 1;
}

.player-header-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    background: #f8f9fa;
    border-radius: 8px;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 16px;
}

.player-profile {
    display: flex;
    align-items: center;
    gap: 12px;
}

.profile-avatar {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    object-fit: cover;
}

.profile-avatar-placeholder {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: #e0e0e0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    font-weight: 600;
    color: #616161;
}

.profile-info {
    display: flex;
    flex-direction: column;

    .player-name {
        font-weight: 600;
        font-size: 16px;
    }

    .team-name {
        font-size: 13px;
        color: #6c757d;
    }
}

.injury-summary {
    display: flex;
    align-items: center;
    gap: 12px;
}

.injury-type-badge {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    color: #495057;

    i {
        color: #dc3545;
    }
}

.dashboard-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 12px;
}

.header-info {
    h3 {
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 0 0 4px 0;
        font-size: 20px;

        i {
            color: #007bff;
        }
    }

    p {
        margin: 0;
        color: #6c757d;
        font-size: 13px;
    }
}

.header-actions {
    display: flex;
    gap: 8px;
}

/* Summary Stats */
.summary-stats {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 24px;
}

.stat-card {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 20px;
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    min-width: 140px;

    .stat-icon {
        width: 48px;
        height: 48px;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
        font-weight: 700;

        i {
            font-size: 22px;
        }
    }

    .stat-content {
        display: flex;
        flex-direction: column;

        .stat-value {
            font-size: 24px;
            font-weight: 700;
            line-height: 1.2;
        }

        .stat-label {
            font-size: 12px;
            color: #6c757d;
        }
    }

    &.total {
        .stat-icon {
            background: #e3f2fd;
            color: #1976d2;
        }
    }

    &.overdue {
        .stat-icon {
            background: #ffebee;
            color: #c62828;
        }
    }

    &.circa-phv {
        .stat-icon {
            background: #fff3e0;
            color: #f57c00;
        }
    }
}

/* Filters Section */
.filters-section {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 20px;
    margin-bottom: 24px;
    padding: 16px 20px;
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.filter-group {
    display: flex;
    flex-direction: column;
    gap: 6px;

    label {
        font-size: 12px;
        font-weight: 500;
        color: #6c757d;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }

    select {
        min-width: 180px;
    }

    .view-toggle {
        .btn {
            padding: 8px 16px;
            font-size: 13px;
        }
    }
}

/* Grouped View */
.grouped-view {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.phase-group {
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    overflow: hidden;
    padding: 20px;

    &.empty {
        opacity: 0.6;
    }
}

.group-header {
    padding: 16px 20px;
    border-left: 4px solid;
    background: #f8f9fa;

    .group-title {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 4px;

        h3 {
            margin: 0;
            font-size: 16px;
            font-weight: 600;
        }

        .player-count {
            font-size: 13px;
            color: #6c757d;
            background: white;
            padding: 2px 10px;
            border-radius: 12px;
        }
    }

    .group-description {
        margin: 0;
        font-size: 13px;
        color: #6c757d;
    }
}

.no-players {
    padding: 24px;
    text-align: center;
    color: #6c757d;
    font-size: 14px;
}

/* Table View */
.table-view {
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    overflow: hidden;
}

.view-toggle {
    display: flex;
    gap: 4px;
}

.loading-state,
.empty-state {
    text-align: center;
    padding: 40px;
    color: #6c757d;

    i {
        font-size: 40px;
        opacity: 0.5;
        margin-bottom: 8px;
    }
}

.loading-state {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
}

/* Cards Grid */
.players-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 14px;
}

.player-card {
    background: white;
    border-radius: 10px;
    padding: 14px;
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s;
    border: 2px solid transparent;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    }

    &.overdue {
        border-color: #dc3545;
    }

    &.circa-phv {
        border-color: #ffc107;
    }

    .card-header {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 12px;

        .player-info {
            flex: 1;

            .player-name {
                display: block;
                font-weight: 600;
                font-size: 14px;
            }

            .team-name {
                display: block;
                font-size: 12px;
                color: #6c757d;
            }
        }

        .maturity-flag {
            color: #f57c00;
            font-size: 16px;
        }
    }

    .injury-type {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 13px;
        color: #495057;
        margin-bottom: 12px;

        i {
            color: #dc3545;
        }
    }

    .card-stats {
        display: flex;
        gap: 16px;
        margin-bottom: 12px;

        .stat {
            display: flex;
            flex-direction: column;
            gap: 2px;

            .stat-label {
                font-size: 11px;
                color: #6c757d;
                text-transform: uppercase;
            }

            .stat-value {
                font-size: 14px;
                font-weight: 600;

                &.warning {
                    color: #f57c00;
                }
            }
        }
    }

    .review-status {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 12px;
        background: #e8f5e9;
        color: #2e7d32;
        border-radius: 6px;
        font-size: 12px;

        i {
            font-size: 14px;
        }

        &.overdue {
            background: #ffebee;
            color: #c62828;
        }
    }
}

.card-top {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
}

.player-avatar,
.avatar-sm {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    object-fit: cover;
}

.avatar-sm {
    width: 28px;
    height: 28px;
}

.player-avatar-placeholder,
.avatar-placeholder-sm {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #e0e0e0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 600;
    color: #616161;
}

.avatar-placeholder-sm {
    width: 28px;
    height: 28px;
    font-size: 10px;
}

.player-info {
    flex: 1;
    min-width: 0;
}

.player-name {
    display: block;
    font-weight: 600;
    font-size: 14px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.team-name {
    display: block;
    font-size: 11px;
    color: #6c757d;
}

.phase-badge {
    padding: 4px 10px;
    border-radius: 12px;
    color: white;
    font-size: 11px;
    font-weight: 600;
}

.phase-badge-sm {
    padding: 2px 8px;
    border-radius: 8px;
    color: white;
    font-size: 10px;
    font-weight: 600;
}

.card-body {
    font-size: 13px;
}

.injury-label {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;
    color: #495057;

    i {
        color: #dc3545;
    }
}

.card-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 8px;
    font-size: 12px;
    color: #6c757d;

    i {
        margin-right: 4px;
    }
}

.maturity-tag {
    color: #f57c00;

    i {
        color: #f57c00;
    }
}

.review-indicator {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    background: #e8f5e9;
    color: #2e7d32;
    border-radius: 6px;
    font-size: 12px;

    &.overdue {
        background: #ffebee;
        color: #c62828;
    }
}

/* List View */
.list-view {
    background: white;
    border-radius: 10px;
    overflow: hidden;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

.rtp-list-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    th,
    td {
        padding: 10px 12px;
        text-align: left;
        border-bottom: 1px solid #e9ecef;
    }

    th {
        background: #f8f9fa;
        font-weight: 600;
        font-size: 12px;
        white-space: nowrap;
    }

    tbody tr {
        cursor: pointer;
        transition: background 0.15s;

        &:hover {
            background: #f8f9fa;
        }
    }

    .overdue-row {
        background: #fff5f5;
    }
}

.player-cell {
    display: flex;
    align-items: center;
    gap: 8px;
}

.maturity-icon {
    color: #f57c00;
    font-size: 14px;
}

/* Modal */
.modal-tabs {
    display: flex;
    gap: 8px;
    margin-bottom: 16px;
    border-bottom: 1px solid #e9ecef;
    padding-bottom: 12px;
}

.tab-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border: none;
    background: #f8f9fa;
    border-radius: 6px;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background: #e9ecef;
    }

    &.active {
        background: #007bff;
        color: white;
    }
}

.modal-content-area {
    min-height: 300px;
}

/* Detail Tabs */
.detail-tabs {
    margin-bottom: 20px;

    .nav-link {
        display: flex;
        align-items: center;
        gap: 6px;
        color: #6c757d;
        padding: 10px 16px;
        border: none;
        border-bottom: 2px solid transparent;
        transition: all 0.2s;

        i {
            font-size: 16px;
        }

        &:hover {
            color: #007bff;
            border-color: transparent;
        }

        &.active {
            color: #007bff;
            font-weight: 500;
            border-bottom-color: #007bff;
            background: transparent;
        }
    }
}

/* Status Bar */
.status-bar {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    padding: 16px;
    background: #f8f9fa;
    border-radius: 8px;
    margin-bottom: 16px;
}

.status-item {
    display: flex;
    align-items: center;
    gap: 8px;

    label {
        font-size: 13px;
        font-weight: 500;
        color: #666;
        margin: 0;
    }

    select {
        min-width: 130px;
    }

    .status-value {
        font-weight: 600;
        color: #333;
    }
}

/* Injury Details Content */
.injury-details-content {
    padding: 16px 0;
}

.detail-section {
    margin-bottom: 24px;

    h5 {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 15px;
        font-weight: 600;
        color: #333;
        margin-bottom: 16px;
        padding-bottom: 8px;
        border-bottom: 1px solid #eee;

        i {
            color: #007bff;
        }
    }
}

.detail-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 16px;
}

.detail-item {
    display: flex;
    flex-direction: column;
    gap: 4px;

    label {
        font-size: 12px;
        color: #666;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin: 0;
    }

    span {
        font-size: 14px;
        color: #333;
    }
}

.status-good {
    color: #28a745 !important;
    font-weight: 600;
}

.status-warning {
    color: #ffc107 !important;
    font-weight: 600;
}

.status-bad {
    color: #dc3545 !important;
    font-weight: 600;
}

.phase-badge-inline {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 12px;
    color: white;
    font-size: 13px;
    font-weight: 500;
}

.injury-notes,
.restrictions-text {
    font-size: 14px;
    color: #555;
    line-height: 1.6;
    padding: 12px;
    background: #f8f9fa;
    border-radius: 8px;
    margin: 0;
}

.restrictions-text {
    background: #fff3cd;
    border: 1px solid #ffeeba;
    color: #856404;
}

/* Physio Appointments Tab */
.physio-appointments-content {
    padding: 16px 0;
}

.appointments-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    h5 {
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 0;
        font-size: 16px;
        font-weight: 600;

        i {
            color: #007bff;
        }
    }
}

.empty-appointments {
    text-align: center;
    padding: 40px 20px;
    color: #6c757d;

    i {
        font-size: 48px;
        opacity: 0.4;
        margin-bottom: 12px;
        display: block;
    }

    p {
        margin-bottom: 16px;
        font-size: 14px;
    }
}

.appointments-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.appointment-card {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px;
    background: white;
    border-radius: 10px;
    border: 1px solid #e9ecef;
    transition: box-shadow 0.2s;

    &:hover {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    &.confirmed,
    &.booked {
        border-left: 4px solid #28a745;
    }

    &.pending {
        border-left: 4px solid #ffc107;
    }

    &.cancelled {
        border-left: 4px solid #dc3545;
        opacity: 0.7;
    }
}

.apt-date-badge {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    background: #f8f9fa;
    border-radius: 10px;
    flex-shrink: 0;

    .apt-day {
        font-size: 22px;
        font-weight: 700;
        color: #333;
        line-height: 1;
    }

    .apt-month {
        font-size: 11px;
        color: #6c757d;
        text-transform: uppercase;
        font-weight: 600;
    }
}

.apt-details {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;

    .apt-time,
    .apt-physio,
    .apt-reason {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 13px;
        color: #555;

        i {
            color: #6c757d;
            font-size: 14px;
            width: 27px;
        }
    }

    .apt-time {
        font-weight: 600;
        color: #333;
    }

    .apt-reason {
        font-style: italic;
        color: #6c757d;
    }
}

.apt-status {
    .status-badge {
        display: inline-block;
        padding: 4px 12px;
        border-radius: 12px;
        font-size: 11px;
        font-weight: 600;
        text-transform: capitalize;

        &.confirmed,
        &.booked {
            background: #d4edda;
            color: #155724;
        }

        &.pending {
            background: #fff3cd;
            color: #856404;
        }

        &.cancelled {
            background: #f8d7da;
            color: #721c24;
        }
    }
}

.appointment-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    background: #28a745;
    color: white;
    border-radius: 9px;
    font-size: 10px;
    font-weight: 600;
    margin-left: 6px;
}

/* Responsive */
@media (max-width: 576px) {
    .dashboard-header {
        flex-direction: column;
        align-items: stretch;
    }

    .header-actions {
        justify-content: flex-end;
    }

    .players-grid {
        grid-template-columns: 1fr;
    }

    .list-view {
        overflow-x: auto;

        .rtp-list-table {
            min-width: 600px;
        }
    }

    .status-bar {
        flex-direction: column;
        gap: 12px;
    }

    .detail-grid {
        grid-template-columns: 1fr;
    }
}
</style>
